import { Text } from "@react-three/drei";
import { useGameStore } from "../game/useGameStore";
import {
  colors,
  overlay,
  primaryButton,
  secondaryButton,
  typography,
} from "./theme";

interface SurvivalIntroOverlayProps {
  onStart: () => void;
  onMenu: () => void;
}

export function SurvivalIntroOverlay({
  onStart,
  onMenu,
}: SurvivalIntroOverlayProps) {
  const phase = useGameStore((state) => state.phase);
  const best = useGameStore((state) => state.survivalBest);
  const audioStatus = useGameStore((state) => state.survivalAudioStatus);
  const audioLoaded = useGameStore((state) => state.survivalAudioLoaded);
  const audioTotal = useGameStore((state) => state.survivalAudioTotal);
  if (phase !== "survival-intro") return null;

  const canStart = audioStatus === "ready" || audioStatus === "error";
  const startLabel =
    audioStatus === "ready"
      ? "Start"
      : audioStatus === "error"
        ? "Retry audio"
        : `Preparing audio ${audioLoaded}/${audioTotal}`;

  return (
    <main style={introOverlay}>
      <section style={introContent}>
        <div style={eyebrow}>無限</div>
        <h1 style={title}>SURVIVAL</h1>
        <p style={rules}>
          Endless. It only gets faster. Survive - miss too many and it ends.
          Chase the high score.
        </p>
        <div style={bestLabel}>LOCAL HIGH SCORE</div>
        <div style={bestScore}>{best.toLocaleString("en-US")}</div>
        <div style={actions}>
          <button onClick={onMenu} style={menuButton}>
            Menu
          </button>
          <button
            onClick={onStart}
            style={{
              ...startButton,
              ...(!canStart ? disabledStartButton : {}),
            }}
            disabled={!canStart}
          >
            {startLabel}
          </button>
        </div>
      </section>
    </main>
  );
}

export function SurvivalIntro3D() {
  const phase = useGameStore((state) => state.phase);
  const best = useGameStore((state) => state.survivalBest);
  const audioStatus = useGameStore((state) => state.survivalAudioStatus);
  const audioLoaded = useGameStore((state) => state.survivalAudioLoaded);
  const audioTotal = useGameStore((state) => state.survivalAudioTotal);
  if (phase !== "survival-intro") return null;

  const instruction =
    audioStatus === "ready"
      ? "A / X Start - B / Y Menu"
      : audioStatus === "error"
        ? "A / X Retry Audio - B / Y Menu"
        : `PREPARING AUDIO ${audioLoaded}/${audioTotal}`;

  return (
    <group position={[0, 1.62, -2.25]}>
      <mesh position={[0, 0, -0.02]}>
        <planeGeometry args={[2.2, 1.48]} />
        <meshBasicMaterial
          color={colors.ink}
          transparent
          opacity={0.86}
          depthWrite={false}
        />
      </mesh>
      <Text
        position={[0, 0.52, 0]}
        fontSize={0.105}
        color={colors.amber}
        anchorX="center"
        anchorY="middle"
      >
        無限 SURVIVAL
      </Text>
      <Text
        position={[0, 0.15, 0]}
        fontSize={0.052}
        maxWidth={1.68}
        lineHeight={1.45}
        textAlign="center"
        color={colors.washi}
        anchorX="center"
        anchorY="middle"
      >
        Endless. It only gets faster.{"\n"}Survive - miss too many and it ends.
        {"\n"}Chase the high score.
      </Text>
      <Text
        position={[0, -0.22, 0]}
        fontSize={0.04}
        color={colors.steel}
        anchorX="center"
        anchorY="middle"
      >
        LOCAL HIGH SCORE
      </Text>
      <Text
        position={[0, -0.38, 0]}
        fontSize={0.13}
        color={colors.gold}
        anchorX="center"
        anchorY="middle"
      >
        {best.toString().padStart(6, "0")}
      </Text>
      <Text
        position={[0, -0.62, 0]}
        fontSize={0.04}
        color={colors.border}
        anchorX="center"
        anchorY="middle"
      >
        {instruction}
      </Text>
    </group>
  );
}

const introOverlay: React.CSSProperties = {
  ...overlay,
  display: "grid",
  placeItems: "center",
  padding: 24,
  boxSizing: "border-box",
};

const introContent: React.CSSProperties = {
  width: "min(560px, 100%)",
  textAlign: "center",
  containerType: "inline-size",
};

const eyebrow: React.CSSProperties = {
  ...typography.display,
  color: colors.amber,
  fontSize: 34,
  marginBottom: 8,
};

const title: React.CSSProperties = {
  ...typography.ui,
  color: colors.washi,
  fontSize: "clamp(34px, 11cqi, 52px)",
  letterSpacing: "0.12em",
  margin: 0,
};

const rules: React.CSSProperties = {
  color: colors.steel,
  fontSize: 17,
  lineHeight: 1.7,
  margin: "30px auto 36px",
  maxWidth: 500,
};

const bestLabel: React.CSSProperties = {
  ...typography.ui,
  color: colors.border,
  fontSize: 12,
};

const bestScore: React.CSSProperties = {
  ...typography.score,
  color: colors.gold,
  fontSize: 46,
  marginTop: 8,
};

const actions: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 2fr",
  gap: 12,
  marginTop: 36,
};

const menuButton: React.CSSProperties = {
  ...secondaryButton,
  padding: "15px 24px",
};

const startButton: React.CSSProperties = {
  ...primaryButton,
  padding: "15px 24px",
  fontSize: 17,
};

const disabledStartButton: React.CSSProperties = {
  cursor: "wait",
  opacity: 0.55,
};
